/**
 * 「想要」活动与素材兼容性（与 App `PostKit/topicMediaCompat` / 小程序发帖页对齐）
 * - 活动列表展示：任一素材在该活动的 wantableTopics 中即展示（并集）
 * - 不兼容素材在选中活动后置灰，而非从列表中隐藏活动
 */

export function includesTopicId(
  wantableTopics: Array<{ uniqueTopicId: string }> | undefined,
  topicId: string,
  allowPostWantByTopicIds: string[] = []
): boolean {
  if (!wantableTopics?.length) return false
  return wantableTopics.some(
    t => t.uniqueTopicId === topicId || allowPostWantByTopicIds.includes(t.uniqueTopicId)
  )
}

export type TopicLikeForMedia = {
  uniqueTopicId?: string
  wantable?: number
  allowPostWantByTopicIds?: string[]
}

/** 素材是否匹配当前选中活动（含旧数据：仅 uniqueTopicId / allowPostWantByTopicIds） */
export function mediaSupportsWantableTopic(
  item: Record<string, unknown>,
  selectedTopicId: string,
  allowPostWantByTopicIds: string[]
): boolean {
  if (!selectedTopicId) return true
  if (
    includesTopicId(
      item.wantableTopics as Array<{ uniqueTopicId: string }> | undefined,
      selectedTopicId,
      allowPostWantByTopicIds
    )
  ) {
    return true
  }
  const uid = item.uniqueTopicId as string | undefined
  if (allowPostWantByTopicIds.length > 0) {
    return selectedTopicId === uid || (!!uid && allowPostWantByTopicIds.includes(uid))
  }
  return selectedTopicId === uid
}

export function isTopicCompatibleWithMedia(
  topic: TopicLikeForMedia,
  mediaList: Record<string, unknown>[]
): boolean {
  if (topic.wantable !== 1) return true
  if (mediaList.length === 0) return true
  const tid = topic.uniqueTopicId || ''
  const allowed = topic.allowPostWantByTopicIds || []
  return mediaList.some(item => {
    if ((item.wantableTopics as { length?: number } | undefined)?.length) {
      return includesTopicId(item.wantableTopics as Array<{ uniqueTopicId: string }>, tid, allowed)
    }
    const uid = item.uniqueTopicId as string | undefined
    if (allowed.length > 0) {
      return uid === tid || (!!uid && allowed.includes(uid))
    }
    return uid === tid
  })
}

export function filterTopicsByMedia<T extends TopicLikeForMedia>(
  topics: T[],
  mediaList: Record<string, unknown>[]
): T[] {
  return topics.filter(t => isTopicCompatibleWithMedia(t, mediaList))
}
